import React, { useEffect, useState, useMemo } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { returnAssignment } from "api/assignments";

/* ── Иконки ─────────────────────────────────────────────── */
const UserIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
      clipRule="evenodd"
    />
  </svg>
);
const ItemIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M5 8a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" />
    <path
      fillRule="evenodd"
      d="M3 3a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
      clipRule="evenodd"
    />
  </svg>
);
const ArrowDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414l-3-3z"
      clipRule="evenodd"
    />
  </svg>
);

/* ── Компонент ─────────────────────────────────────────── */
export const ReturnModal = ({ open, onClose, assignment, onReturned }) => {
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Остаток к возврату с защитой от NaN
  const remaining = useMemo(() => {
    const issued = Number(assignment?.qty || 0);
    const already = Number(assignment?.returned_qty || 0);
    return Math.max(0, issued - already);
  }, [assignment]);

  // При открытии — ставим максимум доступного к возврату
  useEffect(() => {
    if (!open || !assignment) return;
    setQty(remaining > 0 ? remaining : 1);
    setError(null);
  }, [open, assignment, remaining]);

  const submit = async () => {
    const max = remaining;
    const val = Number(qty) || 0;

    if (val <= 0 || val > max) {
      setError(`Miqdor 1 dan ${max} gacha bo'lishi kerak`);
      return;
    }

    // безопасно определяем id назначения
    const safeAssignmentId = Number(
      assignment?.id ?? assignment?.assignment_id
    );
    if (!safeAssignmentId) {
      setError("Topshiriq ID si topilmadi");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await returnAssignment({ assignment_id: safeAssignmentId, qty: val });
      onReturned?.();
      onClose();
    } catch (e) {
      // Текст ошибки из функции/политик
      const msg = e?.message || e?.hint || "Qaytarishni amalga oshirib bo'lmadi";
      setError(msg);
      console.error("Return error:", e);
    } finally {
      setLoading(false);
    }
  };

  const unit = assignment?.item_unit || "шт";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Qaytarishni rasmiylashtirish"
      className="bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-slate-100"
      footer={
        <div className="flex items-center justify-between w-full">
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Qaytarilishi kerak:{" "}
            <span className="font-semibold text-gray-700 dark:text-slate-200">
              {remaining} {unit}
            </span>
          </p>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading} className="text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-800">
              Bekor qilish
            </Button>
            <Button
              onClick={submit}
              disabled={loading || remaining <= 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Qaytarilmoqda..." : "Rasmiylashtirish"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <InfoCard
          icon={<UserIcon />}
          title={assignment?.user_name || assignment?.full_name || "—"}
          subtitle="Kimdan"
        />

        <div className="flex justify-center text-gray-400 dark:text-slate-500">
          <ArrowDownIcon />
        </div>

        <InfoCard
          icon={<ItemIcon />}
          title={assignment?.item_name || "—"}
          subtitle="Nima qaytarilmoqda"
        />

        <div className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="font-semibold text-gray-800 dark:text-slate-200">
              Qaytariladigan miqdor
            </label>
            <Button
              variant="ghost"
              onClick={() => setQty(remaining || 1)}
              className="text-sm h-auto py-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              disabled={remaining <= 0}
            >
              Hammasini qaytarish
            </Button>
          </div>
          <QuantityInput
            value={qty}
            onChange={setQty}
            maxValue={remaining || 1}
            disabled={remaining <= 0}
          />
        </div>

        {error && (
          <p className="text-center text-red-600 text-sm font-medium pt-2">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
};

/* ── Локальные UI-хелперы ───────────────────────────────── */
const InfoCard = ({ icon, title, subtitle }) => (
  <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-gray-200 dark:border-slate-700 flex items-center gap-4">
    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 rounded-lg">
      {icon}
    </div>
    <div>
      <p className="text-xs text-gray-500 dark:text-slate-400">{subtitle}</p>
      <p className="font-semibold text-gray-800 dark:text-slate-100 text-lg">{title}</p>
    </div>
  </div>
);

const QuantityInput = ({ value, onChange, maxValue = 1, disabled = false }) => {
  const clamp = (v) =>
    Math.max(1, Math.min(Number(maxValue) || 1, Number(v) || 1));
  const step = (d) => onChange(clamp((Number(value) || 0) + d));

  return (
    <div className="relative flex items-center">
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={disabled}
        className="absolute left-0 h-full px-4 text-gray-600 dark:text-slate-400 disabled:text-gray-300 dark:disabled:text-slate-600 hover:text-black dark:hover:text-white transition-colors"
      >
        &minus;
      </button>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(clamp(e.target.value))}
        min="1"
        max={maxValue}
        disabled={disabled}
        className="w-full h-14 bg-white dark:bg-slate-800 disabled:bg-gray-100 dark:disabled:bg-slate-800/50 border-2 border-gray-300 dark:border-slate-600 rounded-lg shadow-sm text-center font-mono text-2xl text-gray-900 dark:text-slate-100 focus:ring-blue-500 focus:border-blue-500 transition"
      />
      <button
        type="button"
        onClick={() => step(1)}
        disabled={disabled}
        className="absolute right-0 h-full px-4 text-gray-600 dark:text-slate-400 disabled:text-gray-300 dark:disabled:text-slate-600 hover:text-black dark:hover:text-white transition-colors"
      >
        &plus;
      </button>
    </div>
  );
};
